import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getApiBaseUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
} from '../ui/index';

/**
 * Login Form Component
 * Provides admin authentication interface
 */

// Login form validation schema
const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required')
    .max(50, 'Username must be less than 50 characters'),
  password: z
    .string()
    .min(1, 'Password is required')
    .max(100, 'Password must be less than 100 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
  className?: string;
}

export function LoginForm({ onSuccess, className }: LoginFormProps) {
  const { login, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data.username, data.password);
      onSuccess?.();
    } catch (error) {
      // Error is handled by AuthContext, but we can add form-specific error handling
      // Note: Form fields are NOT reset to preserve user input on error
      if (error instanceof Error) {
        if (error.message.includes('username')) {
          setError('username', { message: 'Invalid username' });
        } else if (error.message.includes('password')) {
          setError('password', { message: 'Invalid password' });
        }
      }
    }
  };

  const handleQuickLogin = async (username: string, password: string) => {
    setValue('username', username);
    setValue('password', password);

    await onSubmit({ username, password });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Card className={`w-full max-w-md mx-auto ${className}`}>
      <CardHeader className='space-y-1'>
        <CardTitle className='text-2xl font-bold text-center'>
          Admin Login
        </CardTitle>
        <CardDescription className='text-center'>
          Enter your credentials to access the admin portal
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          {/* Global error message */}
          {error && (
            <Alert variant='destructive' data-testid='login-error-alert'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                <div className='space-y-1'>
                  <div className='font-semibold'>Login Failed</div>
                  <div className='text-sm'>{error}</div>
                  {error.includes('CORS') || error.includes('Network') ? (
                    <div className='text-xs mt-2 p-2 bg-red-50 rounded'>
                      <strong>Network Issue:</strong> Check if backend is
                      running at {getApiBaseUrl()}
                    </div>
                  ) : null}
                  {error.includes('401') || error.includes('Unauthorized') ? (
                    <div className='text-xs mt-2 p-2 bg-red-50 rounded'>
                      <strong>Error Code: 401</strong> - Invalid credentials.
                      Dev/mock mode: username <strong>admin</strong>, password{' '}
                      <strong>admin</strong>.
                    </div>
                  ) : null}
                  {error.includes('403') || error.includes('Forbidden') ? (
                    <div className='text-xs mt-2 p-2 bg-red-50 rounded'>
                      <strong>Error Code: 403</strong> - Access denied. User may
                      not have required permissions.
                    </div>
                  ) : null}
                  {error.includes('500') ||
                  error.includes('Internal Server') ? (
                    <div className='text-xs mt-2 p-2 bg-red-50 rounded'>
                      <strong>Error Code: 500</strong> - Server error. Please
                      try again later.
                    </div>
                  ) : null}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Username field */}
          <div className='space-y-2'>
            <Label htmlFor='username'>Username</Label>
            <Input
              id='username'
              data-testid='login-username-input'
              type='text'
              placeholder='admin'
              autoComplete='username'
              {...register('username')}
              className={errors.username ? 'border-red-500' : ''}
            />
            {errors.username && (
              <p className='text-sm text-red-500'>{errors.username.message}</p>
            )}
          </div>

          {/* Password field */}
          <div className='space-y-2'>
            <Label htmlFor='password'>Password</Label>
            <div className='relative'>
              <Input
                id='password'
                data-testid='login-password-input'
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter your password'
                autoComplete='current-password'
                {...register('password')}
                className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
              />
              <button
                type='button'
                onClick={togglePasswordVisibility}
                className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
            {errors.password && (
              <p className='text-sm text-red-500'>{errors.password.message}</p>
            )}
            {!errors.password && (
              <p className='text-xs text-gray-500'>
                Minimum 1 character required
              </p>
            )}
          </div>

          {/* Submit button */}
          <Button
            type='submit'
            data-testid='login-submit-btn'
            className='w-full'
            disabled={isLoading || isSubmitting}
          >
            {isLoading || isSubmitting ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                Signing in...
              </>
            ) : (
              <>
                <LogIn className='h-4 w-4 mr-2' />
                Sign In
              </>
            )}
          </Button>
        </form>

        {/* Additional info */}
        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-600'>
            Forgot your password?{' '}
            <button
              type='button'
              className='text-primary hover:underline font-medium'
              onClick={() => {
                // TODO: Implement forgot password functionality
                alert(
                  'Forgot password functionality will be implemented soon.'
                );
              }}
            >
              Reset it here
            </button>
          </p>
        </div>

        {/* Development info */}
        {(import.meta.env.DEV ||
          window.location.hostname.includes('localhost') ||
          window.location.hostname.includes('127.0.0.1') ||
          window.location.hostname.includes('ngrok') ||
          window.location.hostname.includes('dev')) && (
          <div className='mt-4 p-3 bg-gray-100 rounded-md space-y-2'>
            <p className='text-xs text-gray-600 text-center'>
              <strong>Development Mode:</strong>
              <br />
              Use quick login for local testing.
            </p>
            <div className='flex flex-wrap justify-center gap-2 text-xs'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => handleQuickLogin('admin', 'admin')}
              >
                Admin (admin/admin)
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LoginForm;
